import * as React from 'react';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import ButtonsContainer from '../../ButtonsContainer';
import ReportFormContent from './ReportFormContent';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { useReactToPrint } from 'react-to-print';
import { ReportEntity } from '../../../store/entities/state';
import ReportHeader from './ReportHeader';
import { parseDateString } from '../../../shared/utils';
import { useReportTemplate } from '../../../hooks/useReportTemplate';
import { Box, CircularProgress } from '@material-ui/core';
import AppButton from '../../AppButton';

export interface AiScriberWarningItem {
    type: string;
    message: string;
}

export interface ReportFormStateProps {
    companyLogo?: string,
    report: ReportEntity,
    hasChanged?: boolean,
    readOnly?: boolean,
    loading?: boolean,
    aiSuggestions?: Record<string, string>,
    aiAdditionalNotes?: string,
    aiWarnings?: AiScriberWarningItem[],
}

const AiAdditionalNotesBox = styled.div`
    margin-top: var(--spacer-S);
    padding: var(--spacer-S);
    background-color: var(--bg-hover-light);
    border: var(--border-thin) solid var(--info-color);
    border-radius: 4px;
    white-space: pre-wrap;
    font-size: 0.9em;
    color: var(--text-body);
`;

const AiAdditionalNotesLabel = styled.div`
    font-weight: 600;
    color: var(--info-color);
    margin-bottom: var(--spacer-XXS);
    font-size: 0.85em;
`;

const warningsPanelStyle: React.CSSProperties = {
    marginBottom: 'var(--spacer-S)',
    padding: 'var(--spacer-XS) var(--spacer-S)',
    backgroundColor: 'var(--warning-bg)',
    border: 'var(--border-thin) solid var(--warning-border)',
    borderRadius: '4px',
};

const warningsHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    fontSize: '0.9em',
    fontWeight: 600,
    color: 'var(--text-body)',
};

const warningsItemStyle: React.CSSProperties = {
    fontSize: '0.85em',
    color: 'var(--text-body)',
    marginTop: 'var(--spacer-XXS)',
    paddingLeft: 'var(--spacer-S)',
};

export interface ReportFormDispatchProps {
    onChange?: (value: ReportEntity) => void,
    onSubmit?: (clientId: string, value: ReportEntity) => void,
    onCopy?: (data: Record<string, string>) => void,
    onClose: () => void,
}

const ReportForm : FC<ReportFormStateProps&ReportFormDispatchProps> = ({
    companyLogo,
    report,
    hasChanged = false,
    readOnly,
    loading,
    onChange,
    onSubmit,
    onClose,
    onCopy,
    aiSuggestions,
    aiAdditionalNotes,
    aiWarnings,
}) => {
    const [warningsExpanded, setWarningsExpanded] = useState(true);
    const t = useTranslations();
    const { template } = useReportTemplate(report.templateId, report.version);

    const reportKey = `${report.id}|${report.templateId}|${report.version}|${report.creationDate}`;
    const [draftReport, setDraftReport] = useState(report);
    const [draftReportKey, setDraftReportKey] = useState(reportKey);
    const [localHasChanged, setLocalHasChanged] = useState(false);
    const dirtyMarkerSentRef = useRef(hasChanged);

    React.useEffect(() => {
        if (draftReportKey === reportKey) return;
        setDraftReport(report);
        setDraftReportKey(reportKey);
        setLocalHasChanged(false);
        dirtyMarkerSentRef.current = hasChanged;
    }, [draftReportKey, hasChanged, report, reportKey]);

    React.useEffect(() => {
        if (hasChanged) {
            dirtyMarkerSentRef.current = true;
        }
    }, [hasChanged]);

    const data = React.useMemo(() => draftReport.values ?? {}, [draftReport]);

    const [errors, setErrors] = useState<{[field: string]: string}>({});

    const draftReportRef = useRef(draftReport);
    draftReportRef.current = draftReport;

    const handleChange = useCallback((field: string, value: any) => {
        const updatedReport = {
            ...draftReportRef.current,
            values: {...draftReportRef.current.values, [field]: value},
        };
        draftReportRef.current = updatedReport;
        setDraftReport(updatedReport);
        setLocalHasChanged(true);
        if (!dirtyMarkerSentRef.current) {
            dirtyMarkerSentRef.current = true;
            onChange && onChange(updatedReport);
        }
        setErrors({});
    }, [onChange]);

    const handleSubmit = useCallback(() => {
        const reportToSubmit = draftReportRef.current;
        const valuesToSubmit = reportToSubmit.values ?? {};
        const missingFields = template?.fields.filter(f => f.isMandatory && !valuesToSubmit[f.label]) ?? [];
        if (missingFields.length) {
            setErrors(missingFields.reduce((prev, curr) => Object.assign(prev, {[curr.label]: t(TK.thisFieldIsMandatory)}), {}));
        } else {
            onSubmit && reportToSubmit && onSubmit(reportToSubmit.clientId, {...reportToSubmit, values: valuesToSubmit});
        }
    }, [onSubmit, t, template]);

    const componentToPrintRef = React.useRef<HTMLDivElement>(null);
    const printZoomFactor = (template?.printZoom ?? 100) / 100;
    const handlePrint = useReactToPrint({
        content: () => componentToPrintRef?.current,
        documentTitle: `[${report?.creationDate?.substr(0, 10)}] ${report?.name} - ${report?.clientName}`,
        pageStyle: printZoomFactor > 1
            ? `@page { margin: 10mm; } body > div { zoom: ${printZoomFactor}; width: ${(100 / printZoomFactor).toFixed(1)}%; }`
            : `@page { margin: 10mm; }`,
    });

    const handleCopyReport = useCallback(() => {
        onCopy && onCopy(data)
    }, [onCopy, data])

    const handleEdit = useCallback(() => {
        onChange && onChange(draftReport)
    }, [onChange, draftReport])

    const canEdit = useMemo(
        () => {
            const d = parseDateString(draftReport.creationDate)
            d.setMinutes(d.getMinutes() + (template?.editableAfterCreationMinutes ?? 0))
            return Date.now() < d.getTime()
        },
        [draftReport.creationDate, template])

    if (!template) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            {!readOnly && aiWarnings && aiWarnings.length > 0 && (
                <div style={warningsPanelStyle}>
                    <div style={warningsHeaderStyle} onClick={() => setWarningsExpanded(x => !x)}>
                        <span>{t(TK.aiScriberWarnings)} ({aiWarnings.length})</span>
                        <span>{warningsExpanded ? '▲' : '▼'}</span>
                    </div>
                    {warningsExpanded && aiWarnings.map((w, i) => (
                        <div key={i} style={warningsItemStyle}>{w.message}</div>
                    ))}
                </div>
            )}
            <div ref={componentToPrintRef} style={{marginLeft: 'var(--spacer-XS)', marginRight: 'var(--spacer-XS)'}}>
                <ReportHeader companyLogo={companyLogo} report={draftReport} template={template} />
                <ReportFormContent
                    fields={template.fields}
                    value={data}
                    onChange={handleChange}
                    readOnly={readOnly}
                    errors={errors}
                    aiSuggestions={!readOnly ? aiSuggestions : undefined} />
                {!readOnly && aiAdditionalNotes && (
                    <AiAdditionalNotesBox>
                        <AiAdditionalNotesLabel>{t(TK.aiScriberAdditionalNotes)}</AiAdditionalNotesLabel>
                        {aiAdditionalNotes}
                    </AiAdditionalNotesBox>
                )}
            </div>
            <ButtonsContainer>
                <AppButton onClick={handlePrint}>{t(TK.print)}</AppButton>
                { !readOnly && <AppButton disabled={!(hasChanged || localHasChanged) || loading} color="success" onClick={handleSubmit}>{t(TK.save)}</AppButton> }
                { readOnly && onCopy && <AppButton onClick={handleCopyReport}>{t(TK.copy)}</AppButton> }
                { readOnly && canEdit && <AppButton onClick={handleEdit}>{t(TK.edit)}</AppButton> }
                <AppButton onClick={onClose}>{t(readOnly ? TK.close : TK.cancel)}</AppButton>
            </ButtonsContainer>
        </>
    );
}

export default ReportForm;
